import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { KeyRound, Plus, RefreshCw, ShieldOff, Stethoscope } from 'lucide-react';
import { api } from '@/lib/api';
import { DeviceType, VENUE_EVENTS } from '@/lib/shared-types';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useRealtime, useRealtimeResync } from '@/Hooks/useRealtime';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import DeviceFormDialog, {
  PairingCodeDialog,
  type IssuedPairing,
} from '@/Pages/Settings/components/DeviceFormDialog';
import {
  FLEET_PERMISSIONS,
  FLEET_ROUTES,
  type DeviceHealth,
  type DeviceVersionPolicy,
  type FleetDevice,
  type FleetHealthRollup,
} from '../fleetApi';
import { assessDevice, type AssessedDevice } from '../fleetHealth';
import {
  DEVICE_HEALTH_VISUALS,
  DeviceHealthChip,
  VersionChip,
  rowStateClass,
} from '../fleetVisuals';
import {
  apiMessage,
  formatDateTime,
  useAutoRefresh,
  useBranches,
  useNow,
  useQueryParams,
} from '../fleetHelpers';
import PanelResult from './PanelResult';
import DeviceDiagnosticsDialog from './DeviceDiagnosticsDialog';
import RevokeDeviceDialog from './RevokeDeviceDialog';

const DEVICE_TYPES = Object.values(DeviceType);
const HEALTH_STATES: DeviceHealth[] = ['HEALTHY', 'DEGRADED', 'OFFLINE', 'NEVER_SEEN', 'REVOKED'];

/** Slower than the spool: a registry changes on the timescale of a shift. */
const REFRESH_MS = 60_000;

/**
 * ## The device registry
 *
 * Type, branch, app version, last seen and health, for every tablet, till, KDS,
 * bridge and terminal in the estate.
 *
 * **Health is computed here, and that is a compromise rather than a design.**
 * ADR-0009 §6 is right that health must be derived at read time — a stored
 * column reports HEALTHY for a tablet that has been in a drawer since Tuesday —
 * and the backend honours it on `/devices/fleet/health` and
 * `/devices/:id/diagnostics`. But `GET /devices` returns the raw row with no
 * verdict, so the list has to apply the rule itself. `fleetHealth.ts` is a
 * deliberate mirror of the backend's `device-health.ts`, tested against the same
 * cases; if the list endpoint ever grows a `health` field, that file goes.
 *
 * **Enrolment is reused, not rebuilt.** `DeviceFormDialog` and
 * `PairingCodeDialog` in Settings already implement the inverted pairing flow —
 * the dashboard names a device and issues a one-time code, the device supplies
 * its own fingerprint — and a second implementation of a one-time credential is
 * how two of them end up disagreeing about when a code expires.
 *
 * **Revocation gets its own dialog** rather than a generic confirmation, because
 * the consequence has to be legible before the button is pressed. See
 * `RevokeDeviceDialog`.
 */
export default function DeviceRegistryPanel() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const confirm = useConfirm();
  const branches = useBranches();
  // Health is a function of elapsed time, so the verdict has to be recomputed as
  // the clock moves and not only when the data changes.
  const now = useNow(30_000);

  const [search, setSearch] = useState('');
  const [branchId, setBranchId] = useState('');
  const [type, setType] = useState('');
  const [health, setHealth] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<FleetDevice | null>(null);
  const [issued, setIssued] = useState<IssuedPairing | null>(null);
  const [diagnosing, setDiagnosing] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<FleetDevice | null>(null);

  const canRegister = can(FLEET_PERMISSIONS.devicesRegister);
  const canRevoke = can(FLEET_PERMISSIONS.devicesRevoke);
  const canDiagnose = can(FLEET_PERMISSIONS.devicesDiagnose);

  const params = useQueryParams({ search, branchId, type, limit: 200 });
  const devicesQuery = useApi<unknown>(FLEET_ROUTES.devices, params);
  const policiesQuery = useApi<DeviceVersionPolicy[]>(FLEET_ROUTES.versionPolicies);
  const rollupQuery = useApi<FleetHealthRollup>(
    FLEET_ROUTES.fleetHealth,
    useQueryParams({ branchId }),
  );

  const reload = () => {
    devicesQuery.reload();
    rollupQuery.reload();
  };

  useAutoRefresh(reload, { enabled: true, intervalMs: REFRESH_MS });

  /**
   * Both device events reach the branch room, gated on `devices.view`.
   *
   * A device with **no branch** is broadcast to the tenant room instead, and
   * this screen does not join that: `room-authorization.ts` grants a tenant room
   * only to a TENANT- or PLATFORM-scoped token, so asking for it from a branch
   * manager's session would simply be refused. Those devices are picked up by
   * the minute timer above rather than being silently missed — which is also why
   * the timer stays even though the events now exist.
   */
  const rooms = useMemo(() => (branchId ? [`branch:${branchId}`] : undefined), [branchId]);
  useRealtime(VENUE_EVENTS.DEVICE_REVOKED, reload, rooms);
  useRealtime(VENUE_EVENTS.DEVICE_UNSUPPORTED_VERSION, reload, rooms);
  useRealtimeResync(reload);

  // In its own memo: a fresh `[]` on every render would re-assess the whole
  // fleet each time, and `assessDevice` runs per row.
  const policies = useMemo(() => policiesQuery.data ?? [], [policiesQuery.data]);
  const assessed = useMemo<AssessedDevice[]>(
    () =>
      rowsOf<FleetDevice>(devicesQuery.data).rows.map((device) =>
        assessDevice(device, policies, now),
      ),
    [devicesQuery.data, policies, now],
  );

  /**
   * The health filter is client-side because the API has no `health` parameter —
   * it filters on `isActive` and `pairing`, which are two of the five states and
   * not the same cut. Applied after the fetch, over the page the server sent.
   */
  const rows = useMemo(() => {
    const filteredRows = health ? assessed.filter((device) => device.health === health) : assessed;
    return [...filteredRows].sort((a, b) => {
      const bySeverity =
        DEVICE_HEALTH_VISUALS[b.health].severity - DEVICE_HEALTH_VISUALS[a.health].severity;
      if (bySeverity !== 0) return bySeverity;
      return a.name.localeCompare(b.name, i18n.language);
    });
  }, [assessed, health, i18n.language]);

  const filtered = Boolean(search || branchId || type || health);
  const clearFilters = () => {
    setSearch('');
    setBranchId('');
    setType('');
    setHealth('');
  };

  /**
   * Re-pairing is what a wiped, reset or replaced device needs: its identifier
   * changed, so the row's old binding is dead. Issuing a fresh code releases
   * that binding server-side and keeps the row — name, branch, history — which
   * is the alternative to deleting and recreating it by hand.
   */
  const reissue = async (device: FleetDevice) => {
    const ok = await confirm.confirm({
      title: t('fleet.devices.repairTitle'),
      body: t('fleet.devices.repairConfirm', { name: device.name }),
      confirmLabel: t('fleet.devices.newCode'),
    });
    if (!ok) return;
    try {
      const { data } = await api.post<IssuedPairing>(FLEET_ROUTES.devicePairing(device.id));
      reload();
      setIssued(data);
    } catch (error) {
      toast.error(apiMessage(error, t('fleet.errors.actionFailed')));
    }
  };

  const rollup = rollupQuery.data;

  return (
    <div className="space-y-4">
      {rollup && (
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {HEALTH_STATES.map((state) => {
            const visual = DEVICE_HEALTH_VISUALS[state];
            const count = rollup.devices.byHealth[state] ?? 0;
            const active = health === state;
            return (
              <button
                key={state}
                type="button"
                aria-pressed={active}
                onClick={() => setHealth(active ? '' : state)}
                className={cn(
                  'rounded-xl border bg-surface p-4 text-start shadow-sm hover:bg-hover',
                  visual.severity >= 4 && count > 0 && 'border-red-300 dark:border-red-900',
                  active && 'ring-2 ring-ring',
                )}
              >
                <p className="flex items-center gap-1.5 text-sm text-ink-muted">
                  <Icon as={visual.icon} size="sm" aria-hidden />
                  {t(`fleet.health.${state}`)}
                </p>
                <p
                  className={cn(
                    'text-2xl font-semibold tabular-nums',
                    visual.severity >= 4 && count > 0 && 'text-red-600 dark:text-red-400',
                  )}
                >
                  {count}
                </p>
              </button>
            );
          })}
        </div>
      )}

      {/* Unsupported versions are the fleet's other failure mode and they do not
          show up as an offline device: the tablet is alive and answering, it is
          simply running a build the tenant will not support. */}
      {rollup && rollup.devices.unsupported.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-4 text-sm dark:border-amber-900 dark:bg-amber-950/30">
          <p className="font-medium">
            {t('fleet.devices.unsupportedBanner', { count: rollup.devices.unsupported.length })}
          </p>
          <p className="mt-1 text-ink-muted">
            {rollup.devices.unsupported
              .map((device) => `${device.name} (${device.version ?? t('fleet.common.unknown')})`)
              .join(' · ')}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[12rem] flex-1">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('fleet.devices.searchPlaceholder')}
            aria-label={t('common.search')}
          />
        </div>
        {branches.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            aria-label={t('fleet.common.branch')}
          >
            <option value="">{t('fleet.common.allBranches')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        )}
        <Select
          className="w-auto min-w-[10rem]"
          value={type}
          onChange={(event) => setType(event.target.value)}
          aria-label={t('fleet.common.type')}
        >
          <option value="">{t('fleet.devices.allTypes')}</option>
          {DEVICE_TYPES.map((option) => (
            <option key={option} value={option}>
              {t(`fleet.deviceType.${option}`)}
            </option>
          ))}
        </Select>
        <Select
          className="w-auto min-w-[10rem]"
          value={health}
          onChange={(event) => setHealth(event.target.value)}
          aria-label={t('fleet.common.health')}
        >
          <option value="">{t('fleet.devices.allHealth')}</option>
          {HEALTH_STATES.map((state) => (
            <option key={state} value={state}>
              {t(`fleet.health.${state}`)}
            </option>
          ))}
        </Select>

        <Button variant="outline" size="sm" onClick={reload} disabled={devicesQuery.loading}>
          <Icon as={RefreshCw} />
          {t('fleet.common.refresh')}
        </Button>
        {canRegister && (
          <Button
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <Icon as={Plus} />
            {t('fleet.devices.enroll')}
          </Button>
        )}
      </div>

      <PanelResult
        loading={devicesQuery.loading && !devicesQuery.data}
        error={devicesQuery.error}
        count={rows.length}
        onRetry={reload}
        subject={t('fleet.devices.subject')}
        filtered={filtered}
        onClearFilters={clearFilters}
        emptyTitle={t('fleet.devices.emptyTitle')}
        emptyHint={t('fleet.devices.emptyHint')}
        emptyAction={
          canRegister ? (
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              <Icon as={Plus} />
              {t('fleet.devices.enroll')}
            </Button>
          ) : undefined
        }
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('fleet.devices.name')}</TH>
                <TH>{t('fleet.common.type')}</TH>
                <TH>{t('fleet.common.branch')}</TH>
                <TH>{t('fleet.common.health')}</TH>
                <TH>{t('fleet.common.version')}</TH>
                <TH>{t('fleet.devices.lastSeen')}</TH>
                <TH className="text-end">{t('fleet.common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {rows.map((device) => {
                const visual = DEVICE_HEALTH_VISUALS[device.health];
                return (
                  <TR key={device.id} className={cn(rowStateClass(visual))}>
                    <TD>
                      <span className="font-medium">{device.name}</span>
                      {device.awaitingPairing && (
                        <p className="text-xs text-ink-muted">
                          {device.pairing?.status === 'EXPIRED'
                            ? t('fleet.devices.codeExpired')
                            : t('fleet.devices.awaitingCode')}
                        </p>
                      )}
                      {device.revokedReason && (
                        <p className="text-xs text-ink-muted">{device.revokedReason}</p>
                      )}
                    </TD>
                    <TD>
                      <Badge tone="neutral">{t(`fleet.deviceType.${device.type}`)}</Badge>
                    </TD>
                    <TD>{device.branch?.name ?? t('fleet.common.noBranch')}</TD>
                    <TD>
                      <DeviceHealthChip health={device.health} />
                    </TD>
                    <TD>
                      <div className="flex flex-col items-start gap-1">
                        <VersionChip compliance={device.version.compliance} />
                        <span className="text-xs text-ink-muted">
                          {device.appVersion ?? t('fleet.diagnostics.neverReported')}
                        </span>
                      </div>
                    </TD>
                    <TD className="text-ink-muted">
                      {device.lastSeenAt
                        ? formatDateTime(device.lastSeenAt, i18n.language)
                        : t('fleet.common.never')}
                    </TD>
                    <TD>
                      <div className="flex flex-wrap justify-end gap-1">
                        {canDiagnose && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('fleet.diagnostics.open')}
                            onClick={() => setDiagnosing(device.id)}
                          >
                            <Icon as={Stethoscope} />
                            <span className="sr-only">{t('fleet.diagnostics.open')}</span>
                          </Button>
                        )}
                        {canRegister && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('fleet.devices.newCode')}
                            onClick={() => void reissue(device)}
                          >
                            <Icon as={KeyRound} className="text-accent" />
                            <span className="sr-only">{t('fleet.devices.newCode')}</span>
                          </Button>
                        )}
                        {canRevoke && device.isActive && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('fleet.devices.revoke')}
                            onClick={() => setRevoking(device)}
                          >
                            <Icon as={ShieldOff} className="text-red-600 dark:text-red-400" />
                            <span className="sr-only">{t('fleet.devices.revoke')}</span>
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>

      <DeviceFormDialog
        open={formOpen}
        device={editing}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
      />
      <PairingCodeDialog issued={issued} onClose={() => setIssued(null)} />
      <DeviceDiagnosticsDialog deviceId={diagnosing} onClose={() => setDiagnosing(null)} />
      <RevokeDeviceDialog
        device={revoking}
        onClose={() => setRevoking(null)}
        onRevoked={reload}
      />
    </div>
  );
}
